import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  LexicalCommand,
  createCommand,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  KEY_DELETE_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from "lexical";
import { useEffect } from "react";
import { $createImageNode } from "../nodes/ImageNode";

// Command to insert an image into the editor
export const INSERT_IMAGE_COMMAND: LexicalCommand<{
  src: string;
  altText: string;
  width?: number;
  height?: number;
}> = createCommand();

// Helper function to handle image file reading and dispatch command
const readImageFile = (file: File, editor: any) => {
  const reader = new FileReader();

  reader.onload = (event) => {
    const base64Src = event.target?.result as string;
    if (base64Src) {
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
        src: base64Src,
        altText: "Pasted Image",
        width: 300,
        height: 200,
      });
    }
  };

  reader.onerror = () => {
    console.error("Failed to read the image file.");
  };

  reader.readAsDataURL(file);
};

// Helper function to handle the paste event
const handlePaste = (event: ClipboardEvent, editor: any) => {
  const items = event.clipboardData?.items;
  if (!items) return;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) {
        readImageFile(file, editor);
        event.preventDefault(); // Prevent the default paste behavior
        break; // Stop after handling the first image
      }
    }
  }
};

export const ImagePastePlugin = () => {
  const [editor] = useLexicalComposerContext(); // Get the Lexical editor instance

  useEffect(() => {
    // Register paste event listener
    const pasteHandler = (event: ClipboardEvent) => handlePaste(event, editor);
    document.addEventListener("paste", pasteHandler);

    // remove event listener when the component unmounts
    return () => {
      document.removeEventListener("paste", pasteHandler);
    };
  }, [editor]); // Re-run the effect when the editor instance changes

  useEffect(() => {
    // Command to insert an image into the editor
    const removeInsertImageCommand = editor.registerCommand(
      INSERT_IMAGE_COMMAND,
      (payload) => {
        const { src, altText, width, height } = payload;
        const imageNode = $createImageNode(src, altText, width, height);

        // Update the editor state to insert the image node
        editor.update(() => {
          const selection = $getSelection();
          if (selection) {
            $insertNodes([imageNode]);
          }
        });
        return true; // Indicate the command was handled
      },
      COMMAND_PRIORITY_HIGH
    );

    // Unregister the command when the component unmounts
    return () => removeInsertImageCommand();
  }, [editor]);

  useEffect(() => {
    // Command to handle deleting an image when selected
    const removeDeleteCommand = editor.registerCommand(
      KEY_DELETE_COMMAND,
      () => {
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const selectedNodes = selection.getNodes();

          // Remove selected image nodes
          editor.update(() => {
            selectedNodes.forEach((node) => {
              if (node.getType() === "image") {
                node.remove();
              }
            });
          });

          return true; // Indicate the command was handled
        }

        return false; // Indicate no action was taken
      },
      COMMAND_PRIORITY_HIGH
    );

    // Unregister the command when the component unmounts
    return () => removeDeleteCommand();
  }, [editor]);

  return null;
};
